import React, { useCallback, useMemo, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import { Col, Grid, Row } from "rsuite";
import {
  AccessControl,
  AsyncSelectPickerInput,
  Button,
  ErrorMessageField,
  Icon,
  Input,
  SelectPickerInput,
} from "@shared/components";
import { SensorListItem } from "@shared/models";
import { PERMISSION, SENSOR_PARAMETERS_OPTIONS } from "@shared/constants";
import { CreateSensorDto, SensorFormShape } from "@containers/Sensor/interfaces";
import { useAllowedHospitalIds, useCheckPermission } from "@shared/hooks";
import { Option } from "@shared/interfaces";

import { getInitValues, getValidationSchema, getHandlers, prepareSubmitValue } from "./formHelpers";

import "./index.scss";

interface SensorFormProps {
  sensor: SensorListItem | null;
  onChangeStatus: (sensorId: number) => void;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateSensorDto) => void;
}

const SensorForm: React.FunctionComponent<SensorFormProps> = (props) => {
  const { sensor, onChangeForm, onCloseForm, onChangeStatus, onSubmitForm } = props;
  const allowedHospitals = useAllowedHospitalIds();
  const formikRef = useRef<FormikProps<SensorFormShape>>(null);

  const formValues = useMemo(
    () => getInitValues(sensor, allowedHospitals.defaultHospitalId),
    [sensor, allowedHospitals.defaultHospitalId],
  );
  const [selectedHospital, setSelectedHospital] = useState<Option | null>(formValues.hospital);
  const validationSchema = useMemo(() => getValidationSchema(sensor), [sensor]);

  const canCreateEditSensor = useCheckPermission(sensor ? PERMISSION.EDIT_SENSOR : PERMISSION.CREATE_SENSOR, {
    onlyAppAdmin: true,
  });

  const handlers = useMemo(() => {
    return getHandlers(selectedHospital);
  }, [selectedHospital]);

  const onChangeSelect = useCallback((field: string, value: Option | null) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(field, value);
    }
  }, []);

  const onChangeParameter = useCallback(
    (value: Option | null) => {
      onChangeSelect("parameter", value);
    },
    [onChangeSelect],
  );

  const onChangeStation = useCallback(
    (value: Option | null) => {
      onChangeSelect("station", value);
    },
    [onChangeSelect],
  );

  const onChangeHospital = useCallback((value: Option | null) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("hospital", value);
      formikRef.current.setFieldValue("station", null);
      setSelectedHospital(value);
    }
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={validationSchema}
      enableReinitialize={true}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        onSubmitForm(prepareSubmitValue(values));
        setSubmitting(false);
      }}
      initialValues={formValues}
      validate={() => {
        onChangeForm();
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit }) => (
        <div className="sensor-form">
          <form onSubmit={handleSubmit}>
            <div className="sensor-form-header">
              <div className="sensor-form-header-left">{sensor ? "Edit Sensor" : "New Sensor"}</div>
              <div className="sensor-form-header-right">
                <AccessControl
                  permission={sensor ? PERMISSION.EDIT_SENSOR : PERMISSION.CREATE_SENSOR}
                  option={{ onlyAppAdmin: true }}
                >
                  <Button type="submit" className="action-btn">
                    {sensor ? "Save" : "Create"}
                  </Button>
                </AccessControl>
                <Button className="close-btn" onClick={() => onCloseForm()}>
                  <Icon type="clear" />
                </Button>
              </div>
            </div>
            <Grid fluid className="sensor-form-content">
              <Row>
                <Col xs={24}>
                  <Input
                    disabled={!canCreateEditSensor}
                    type="text"
                    labelTitle="Sensor ID*"
                    name="sensor_code"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.sensor_code}
                    placeholder="Sensor ID"
                  />
                  <ErrorMessageField name="sensor_code" />
                </Col>
              </Row>
              <Row>
                <Col xs={!sensor ? 24 : 12}>
                  <SelectPickerInput
                    disabled={!canCreateEditSensor}
                    searchable={false}
                    name="parameter"
                    data={SENSOR_PARAMETERS_OPTIONS}
                    className="parameter-input"
                    onChange={onChangeParameter}
                    onClean={handleChange}
                    labelTitle="Parameter*"
                    value={values.parameter}
                    placeholder="Select Parameter*"
                  />
                  <ErrorMessageField name="parameter" />
                </Col>
                {sensor && (
                  <Col xs={6}>
                    <Input disabled type="text" labelTitle="Type" name="type" value={values.type} />
                  </Col>
                )}
              </Row>
              {!sensor?.id && allowedHospitals.hasAccessToMultipleHospitals && (
                <Row>
                  <AsyncSelectPickerInput
                    disabled={!canCreateEditSensor}
                    labelTitle="Hospital*"
                    name="hospital"
                    className="station-input"
                    onChange={onChangeHospital}
                    value={values.hospital}
                    placeholder="Select Hospital*"
                    getData={handlers.hospital.getData}
                    selectData={handlers.hospital.selectData}
                    prepareOptionFunction={handlers.hospital.prepareOptionFunction}
                    loadingAction={handlers.hospital.loadingAction}
                  />
                  <ErrorMessageField name="hospital" />
                </Row>
              )}
              <Row>
                <Col xs={24}>
                  <AsyncSelectPickerInput
                    disabled={!canCreateEditSensor}
                    labelTitle="Station"
                    name="station"
                    className="station-input"
                    onChange={onChangeStation}
                    value={values.station}
                    placeholder="Select Station"
                    getData={handlers.station.getData}
                    selectData={handlers.station.selectData}
                    prepareOptionFunction={handlers.station.prepareOptionFunction}
                    loadingAction={handlers.station.loadingAction}
                  />
                  <ErrorMessageField name="station" />
                </Col>
              </Row>
            </Grid>
          </form>
          <AccessControl permission={PERMISSION.DELETE_SENSOR} option={{ onlyAppAdmin: true }}>
            {sensor ? (
              <div className="sensor-form-footer">
                <Button
                  className={sensor.is_active ? "deactivate" : "activate"}
                  onClick={onChangeStatus.bind(null, Number(sensor.id))}
                >
                  {sensor.is_active ? "Deactivate" : "Activate"}
                </Button>
              </div>
            ) : null}
          </AccessControl>
        </div>
      )}
    </Formik>
  );
};

export default SensorForm;
